/**
 * Consent-aware runtime for Metalwood landing, product, upsell, and cart routes.
 * Commerce destinations are server-rendered; inbound parameters can describe
 * attribution, but never select products, discounts, prices, or routes.
 */

(() => {
  'use strict';

  const core = window.StarmistAttributionCore;
  const STORAGE_KEY = 'starmist.first-touch.v1';
  const TOUCH_STORAGE_KEY = 'starmist.ad-touches.v1';
  const TOUCH_CACHE_LIMIT = 100;
  const PENDING_OFFER_KEY = 'starmist.pending-offer.v1';
  const attributionDiagnostics = document.querySelectorAll('[data-sm-attribution-diagnostic]');
  const privacyDiagnostics = document.querySelectorAll('[data-sm-privacy-diagnostic]');
  const trustedProfileElement = document.querySelector('[data-sm-trusted-profile]');
  const cartSnapshotElement = document.querySelector('[data-sm-cart-snapshot]');
  const cartForm = document.querySelector('[data-sm-cart-attribution]');
  const routeRoot = window.Shopify?.routes?.root || '/';
  const localPath = routeRoot !== '/' && window.location.pathname.startsWith(routeRoot)
    ? `/${window.location.pathname.slice(routeRoot.length)}` : window.location.pathname;
  const isCartPage = Boolean(cartSnapshotElement && localPath === '/cart');

  const state = {
    profile: null,
    privacy: {
      status: 'loading',
      region: 'Undetermined',
      shouldShowBanner: null,
      marketingAllowed: false,
      analyticsAllowed: false,
      preferencesAllowed: false,
      saleOfDataAllowed: false,
    },
    attribution: {
      consent: 'Waiting for Shopify',
      storage: 'Not used',
      source: 'None',
      record: null,
    },
    cart: {
      status: 'Idle',
      ownedAttributes: {},
      unrelatedAttributeCount: 0,
      lastError: '',
    },
  };

  let storageRecord = null;
  let candidateRecord = null;
  let captureSuppressedAfterReset = false;
  let cartQueue = Promise.resolve();
  let consentCleanupPromise = Promise.resolve();
  let funnelViewPublished = false;
  let cartPrimeStarted = false;
  let consentEpoch = 0;
  let cartSnapshotUsable = true;
  let touchCache = { schema: 1, touches: [], pending: [], dropped: 0 };
  let lastAdTouch = null;
  let observedTouchId = '';
  let arrivalConsidered = false;
  let touchOriginReconciled = false;
  const dispatchedTouches = new Set();
  const boundOfferButtons = new WeakSet();
  const boundChapterLinks = new WeakSet();
  const boundProductControls = new WeakSet();

  const livePermissionAllowed = (purpose) => {
    const method = purpose === 'marketing' ? 'marketingAllowed' : 'analyticsProcessingAllowed';
    if (state.privacy.status !== 'ready' || !state.privacy[`${purpose}Allowed`]) return false;

    try {
      return window.Shopify?.customerPrivacy?.[method]?.() === true;
    } catch (_error) {
      return false;
    }
  };

  // An allowed -> denied -> allowed transition must not revive work begun in
  // the earlier consent window, even when a delayed response arrives allowed.
  const marketingConsentIsCurrent = (epoch = consentEpoch) =>
    epoch === consentEpoch && livePermissionAllowed('marketing') && !captureSuppressedAfterReset;

  const approvedEventOrigin = () =>
    marketingConsentIsCurrent() ? state.attribution.record : null;

  const createJourneyId = () => {
    try {
      if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
      if (typeof window.crypto?.getRandomValues !== 'function') return '';

      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch (_error) {
      return '';
    }
  };

  const ensureJourneyId = (record) => {
    if (!record || record.attributes?.sm_journey_id) return record;
    return core?.ensureFirstTouchJourneyId(record, createJourneyId()) || record;
  };

  const publishAnalyticsEvent = (name, payload) => {
    if (
      window.Shopify?.designMode ||
      !livePermissionAllowed('analytics') ||
      !name ||
      !payload ||
      typeof window.Shopify?.analytics?.publish !== 'function'
    ) {
      return false;
    }

    try {
      // Shopify's documented online-store API publishes synchronously to the
      // pixel sandbox. Pixel network delivery is deliberately not part of the
      // shopper's navigation critical path.
      return window.Shopify.analytics.publish(name, payload) === true;
    } catch (_error) {
      return false;
    }
  };

  const publishFunnelViewOnce = () => {
    if (funnelViewPublished || !core || !state.profile) return;
    const payload = core.buildFunnelViewEvent(
      state.profile,
      approvedEventOrigin(),
      state.privacy.region,
    );
    if (!payload) return;

    funnelViewPublished = publishAnalyticsEvent(core.funnelViewEventName(state.profile), withActiveTouch(payload));
  };

  const publishOfferClick = (action) => {
    if (!core || !state.profile || !action) return false;
    const payload = core.buildOfferClickEvent(
      state.profile,
      action,
      approvedEventOrigin(),
      state.privacy.region,
    );
    return publishAnalyticsEvent(core.offerClickEventName(state.profile), withActiveTouch(payload));
  };

  const publishChapterStart = (link) => {
    if (!core || !state.profile || !link) return false;
    const payload = core.buildLandingChapterStartEvent(
      state.profile,
      {
        id: link.dataset.smCtaId,
        placement: link.dataset.smCtaPlacement,
      },
      approvedEventOrigin(),
      state.privacy.region,
    );
    return publishAnalyticsEvent(core.FUNNEL_EVENT_NAMES.landingChapterStarted, withActiveTouch(payload));
  };

  // Additive context only: existing origin fields always remain first touch.
  const withActiveTouch = (payload) => {
    if (!payload || !marketingConsentIsCurrent()) return payload;
    refreshLastAdTouch();
    const touch = core.chooseLastAdTouch([lastAdTouch], payload.journeyId);
    return touch ? { ...payload, activeTouchId: touch.touchId, activeTouchAt: touch.observedAt } : payload;
  };

  const writeDiagnosticValue = (selector, value) => {
    document.querySelectorAll(selector).forEach((output) => {
      output.textContent = value;
    });
  };

  const setDiagnosticStatus = (diagnostics, status) => {
    diagnostics.forEach((diagnostic) => {
      diagnostic.dataset.status = status;
    });
  };

  const yesNo = (value) => (value ? 'Yes' : 'No');

  const ownedAttributesFrom = (attributes) => {
    if (!core || !attributes || typeof attributes !== 'object') return {};

    return core.OWNED_ATTRIBUTE_KEYS.reduce((owned, key) => {
      if (typeof attributes[key] === 'string' && attributes[key]) owned[key] = attributes[key];
      return owned;
    }, {});
  };

  const renderDiagnostics = () => {
    updateCartFormFields();
    writeDiagnosticValue('[data-sm-privacy-region]', state.privacy.region);
    writeDiagnosticValue(
      '[data-sm-privacy-banner]',
      state.privacy.shouldShowBanner === null ? 'Unavailable' : yesNo(state.privacy.shouldShowBanner),
    );
    writeDiagnosticValue('[data-sm-privacy-marketing]', yesNo(state.privacy.marketingAllowed));
    writeDiagnosticValue('[data-sm-privacy-analytics]', yesNo(state.privacy.analyticsAllowed));
    writeDiagnosticValue('[data-sm-privacy-preferences]', yesNo(state.privacy.preferencesAllowed));
    writeDiagnosticValue('[data-sm-privacy-sale-of-data]', yesNo(state.privacy.saleOfDataAllowed));
    setDiagnosticStatus(privacyDiagnostics, state.privacy.status);

    writeDiagnosticValue('[data-sm-attribution-consent]', state.attribution.consent);
    writeDiagnosticValue('[data-sm-attribution-storage]', state.attribution.storage);
    writeDiagnosticValue('[data-sm-attribution-source]', state.attribution.source);
    writeDiagnosticValue('[data-sm-attribution-cart]', state.cart.status);

    const diagnosticPayload = {
      trustedProfile: state.profile,
      privacy: state.privacy,
      firstTouch: state.privacy.marketingAllowed
        ? {
            source: state.attribution.source,
            record: state.attribution.record,
          }
        : {
            source: 'suppressed-by-consent-gate',
            record: null,
          },
      cart: state.cart,
      lastAdTouch: marketingConsentIsCurrent() ? lastAdTouch : null,
      touchDelivery: { pending: touchCache.pending.length, droppedFromLocalCache: touchCache.dropped },
    };

    writeDiagnosticValue('[data-sm-attribution-json]', JSON.stringify(diagnosticPayload, null, 2));
    setDiagnosticStatus(attributionDiagnostics, state.cart.lastError ? 'error' : 'ready');
  };

  const parseTrustedProfile = () => {
    if (!core || !trustedProfileElement) return null;

    try {
      const parsed = JSON.parse(trustedProfileElement.textContent);
      const profile = core.normalizeTrustedProfile(parsed);
      const currentPath = localPath.replace(/^\/collections\/[a-z0-9-]+(?=\/products\/)/, '');
      if (!profile || profile.path !== (profile.surface === 'product' ? currentPath : window.location.pathname)) return null;
      return profile;
    } catch (_error) {
      return null;
    }
  };

  const readStoredRecord = () => {
    try {
      const serialized = window.localStorage.getItem(STORAGE_KEY);
      state.attribution.storage = 'Available';
      if (!serialized) return null;

      const normalized = core.normalizeFirstTouchRecord(JSON.parse(serialized));
      if (!normalized) {
        window.localStorage.removeItem(STORAGE_KEY);
        state.attribution.storage = 'Expired or invalid record cleared';
      }
      return normalized;
    } catch (_error) {
      state.attribution.storage = 'Blocked or unavailable';
      return null;
    }
  };

  const persistRecord = (record) => {
    if (!marketingConsentIsCurrent()) return false;
    const normalized = core.normalizeFirstTouchRecord(record);
    if (!normalized) return false;

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
      state.attribution.storage = 'Saved locally for 90 days';
      storageRecord = normalized;
      return true;
    } catch (_error) {
      state.attribution.storage = 'Blocked; cart remains the fallback';
      return false;
    }
  };

  const removeStoredRecord = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      state.attribution.storage = 'Local record removed';
    } catch (_error) {
      state.attribution.storage = 'Storage unavailable';
    }
    storageRecord = null;
    try { window.sessionStorage?.removeItem(PENDING_OFFER_KEY); } catch (_error) { /* Optional recovery. */ }
    try { window.localStorage.removeItem(TOUCH_STORAGE_KEY); } catch (_error) { /* Optional recovery. */ }
    touchCache = { schema: 1, touches: [], pending: [], dropped: 0 };
    lastAdTouch = null;
    observedTouchId = '';
    arrivalConsidered = false;
    touchOriginReconciled = false;
    dispatchedTouches.clear();
  };

  const readCartSnapshot = () => {
    if (!marketingConsentIsCurrent() || !cartSnapshotUsable || !cartSnapshotElement) return {};
    try {
      const value = JSON.parse(cartSnapshotElement.textContent);
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch (_error) { return {}; }
  };

  const readTouchCache = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(TOUCH_STORAGE_KEY) || 'null');
      if (value?.schema !== 1 || !Array.isArray(value.touches) || !Array.isArray(value.pending)) return null;
      return { schema: 1, touches: value.touches.slice(-TOUCH_CACHE_LIMIT),
        pending: value.pending.filter((id) => typeof id === 'string').slice(-TOUCH_CACHE_LIMIT),
        dropped: Number.isSafeInteger(value.dropped) && value.dropped > 0 ? value.dropped : 0 };
    } catch (_error) { return null; }
  };

  const mergeTouchCache = (other) => {
    const byId = new Map();
    for (const value of [...(other?.touches || []), ...touchCache.touches]) {
      const touch = core.normalizeAdTouch(value);
      if (touch) byId.set(touch.touchId, touch);
    }
    const ordered = [...byId.values()].sort((a, b) =>
      a.observedAt.localeCompare(b.observedAt) || a.touchId.localeCompare(b.touchId));
    const overflow = Math.max(0, ordered.length - TOUCH_CACHE_LIMIT);
    const touches = ordered.slice(-TOUCH_CACHE_LIMIT);
    const retainedIds = new Set(touches.map((touch) => touch.touchId));
    const pending = [...new Set([...(other?.pending || []), ...touchCache.pending])]
      .filter((id) => retainedIds.has(id) && !dispatchedTouches.has(id));
    const dropped = Math.max(Number.isSafeInteger(other?.dropped) && other.dropped > 0 ? other.dropped : 0,
      touchCache.dropped) + overflow;
    touchCache = { schema: 1, touches, pending, dropped };
  };

  const persistTouchCache = () => {
    if (!marketingConsentIsCurrent()) return;
    mergeTouchCache(readTouchCache());
    // Do not queue events across an analytics refusal or replay marketing-only
    // observations if analytics is granted later.
    if (!livePermissionAllowed('analytics')) touchCache.pending = [];
    if (!touchCache.touches.length && !touchCache.dropped) {
      try { window.localStorage.removeItem(TOUCH_STORAGE_KEY); } catch (_error) { /* Optional. */ }
      return;
    }
    try { window.localStorage.setItem(TOUCH_STORAGE_KEY, JSON.stringify(touchCache)); } catch (_error) {
      // The in-memory record and cart attributes still work without storage.
    }
  };

  const refreshLastAdTouch = (cartAttributes = {}) => {
    if (!marketingConsentIsCurrent()) return;
    mergeTouchCache(readTouchCache());
    const journeyId = state.attribution.record?.attributes.sm_journey_id;
    const cartTouch = core.adTouchFromCartAttributes(cartAttributes, journeyId);
    lastAdTouch = core.chooseLastAdTouch([...touchCache.touches, lastAdTouch, cartTouch], journeyId);
  };

  const touchParametersMatch = (left, right) => JSON.stringify(left?.parameters || {}) ===
    JSON.stringify(right?.parameters || {});

  const initializeAdTouches = () => {
    if (!marketingConsentIsCurrent()) return;
    refreshLastAdTouch(readCartSnapshot());
    if (arrivalConsidered || state.profile?.kind !== 'landing' || window.Shopify?.designMode) return;
    arrivalConsidered = true;
    const journeyId = state.attribution.record?.attributes.sm_journey_id;
    const candidate = core.createAdTouch(state.profile, window.location.search, journeyId,
      createJourneyId(), state.privacy.region);
    if (!candidate) return;

    const matching = touchCache.touches.find((touch) => touch.journeyId === journeyId &&
      touch.profile.path === candidate.profile.path && touchParametersMatch(touch, candidate));
    let navigationType = '';
    let internalNavigation = false;
    try { navigationType = window.performance?.getEntriesByType('navigation')?.[0]?.type || ''; } catch (_error) { /* Optional. */ }
    try {
      internalNavigation = Boolean(document.referrer) &&
        new URL(document.referrer).origin === new URL(window.location.href).origin;
    } catch (_error) { /* Missing referrer is not evidence of internal navigation. */ }

    // Never deduplicate by ad ID or time window. A fresh navigation through the
    // same ad is a new touch. Only reload/history replay or internal propagation
    // of identical tags is suppressed when we already observed those tags.
    if ((matching && (navigationType === 'reload' || navigationType === 'back_forward')) ||
      (internalNavigation && lastAdTouch && touchParametersMatch(lastAdTouch, candidate))) return;

    observedTouchId = candidate.touchId;
    touchCache.touches.push(candidate);
    if (livePermissionAllowed('analytics')) touchCache.pending.push(candidate.touchId);
    persistTouchCache();
    refreshLastAdTouch();
  };

  const reconcileTouchOrigin = (cartAttributes = {}) => {
    const journeyId = state.attribution.record?.attributes.sm_journey_id;
    // A fresh cart read can recover an older first touch. Rebind only this
    // document's not-yet-published arrival, never past observations/history.
    touchCache.touches = touchCache.touches.map((touch) =>
      touch.touchId === observedTouchId && !dispatchedTouches.has(touch.touchId)
        ? { ...touch, journeyId } : touch);
    persistTouchCache();
    refreshLastAdTouch(cartAttributes);
  };

  const publishPendingAdTouches = () => {
    if (!touchOriginReconciled || !marketingConsentIsCurrent() || !livePermissionAllowed('analytics')) return;
    const record = approvedEventOrigin();
    const journeyId = record?.attributes.sm_journey_id;
    if (!journeyId) return;
    for (const touch of touchCache.touches) {
      if (touch.journeyId !== journeyId || !touchCache.pending.includes(touch.touchId) ||
        dispatchedTouches.has(touch.touchId)) continue;
      const payload = core.buildAdTouchEvent(touch, record);
      if (payload && publishAnalyticsEvent(core.FUNNEL_EVENT_NAMES.adTouchObserved, payload)) {
        dispatchedTouches.add(touch.touchId);
        touchCache.pending = touchCache.pending.filter((id) => id !== touch.touchId);
      }
    }
    // Shopify accepting publication isn't an ingestion receipt. Exact touch IDs
    // also let the pixel/dashboard collapse any retry of this same observation.
    persistTouchCache();
  };

  const mergeAttributedCart = (existing, journey = {}) => {
    if (marketingConsentIsCurrent()) refreshLastAdTouch(existing);
    const attributes = core.mergeCartAttributes(existing, state.attribution.record, journey) ||
      core.normalizeJourney(journey);
    if (marketingConsentIsCurrent()) Object.assign(attributes, core.adTouchAttributes(lastAdTouch));
    return attributes;
  };

  const pendingOffer = () => {
    if (!marketingConsentIsCurrent()) return null;
    try {
      const value = JSON.parse(window.sessionStorage?.getItem(PENDING_OFFER_KEY) || 'null');
      if (!value || !value.journeyId || value.journeyId !== state.attribution.record?.attributes.sm_journey_id ||
        !Number.isFinite(value.at) || Date.now() < value.at || Date.now() - value.at > 10 * 60 * 1000) return null;
      const journey = core.normalizeJourney({ selectedFormat: value.format });
      return journey.sm_selected_format ? { selectedFormat: journey.sm_selected_format } : null;
    } catch (_error) { return null; }
  };

  const rememberProductOffer = (format) => {
    const journeyId = approvedEventOrigin()?.attributes.sm_journey_id;
    if (!journeyId) return null;
    try {
      const token = JSON.stringify({ journeyId, format, at: Date.now() });
      window.sessionStorage?.setItem(PENDING_OFFER_KEY, token);
      return token;
    } catch (_error) { /* Background cart synchronization remains the fallback. */ }
    return null;
  };

  const pendingOfferToken = () => {
    if (!pendingOffer()) return null;
    try { return window.sessionStorage?.getItem(PENDING_OFFER_KEY); } catch (_error) { return null; }
  };

  const forgetPendingOffer = (token) => {
    try {
      if (token === undefined || (token && window.sessionStorage?.getItem(PENDING_OFFER_KEY) === token)) {
        window.sessionStorage?.removeItem(PENDING_OFFER_KEY);
      }
    } catch (_error) { /* Optional recovery; never interfere with commerce. */ }
  };

  const consumeSyncedOffer = (token, result) => {
    if (result?.status === 'synchronized' || result?.status === 'unchanged') forgetPendingOffer(token);
  };

  const currentCartJourney = () => {
    const pending = pendingOffer();
    if (pending) return pending;
    const attributes = Object.keys(state.cart.ownedAttributes).length ? state.cart.ownedAttributes : readCartSnapshot();
    return {
      selectedFormat: attributes.sm_selected_format,
      upsellKey: attributes.sm_upsell_key,
      upsellVersion: attributes.sm_upsell_version,
      upsellDecision: attributes.sm_upsell_decision,
    };
  };

  // Shopify's native cart form persists attributes in the same POST that starts
  // checkout. Never delay a product form, app upsell, or checkout for analytics.
  const updateCartFormFields = () => {
    if (!cartForm || !core) return;
    const journey = marketingConsentIsCurrent() ? currentCartJourney() : {};
    const attributes = marketingConsentIsCurrent()
      ? mergeAttributedCart({}, journey)
      : {};
    core.OWNED_ATTRIBUTE_KEYS.forEach((key) => {
      let input = cartForm.querySelector(`input[data-sm-cart-field="${key}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `attributes[${key}]`;
        input.dataset.smCartField = key;
        cartForm.appendChild(input);
      }
      input.value = attributes[key] || '';
    });
  };

  const initializeFirstTouch = () => {
    if (!marketingConsentIsCurrent() || (!state.profile && !isCartPage)) return;

    storageRecord = ensureJourneyId(readStoredRecord());
    candidateRecord = null;

    const renderedCartRecord = ensureJourneyId(core.firstTouchFromCartAttributes(readCartSnapshot()));
    if (renderedCartRecord) {
      state.attribution.source = 'Server-rendered cart first touch';
      state.attribution.record = renderedCartRecord;
      persistRecord(renderedCartRecord);
      return;
    }

    if (storageRecord) {
      state.attribution.source = 'Existing 90-day first touch';
      state.attribution.record = storageRecord;
      persistRecord(storageRecord);
      return;
    }

    if (state.profile?.kind === 'landing') {
      candidateRecord = core.createFirstTouchRecord(
        state.profile,
        window.location.search,
        Date.now(),
        createJourneyId(),
        state.privacy.region,
      );
      if (candidateRecord) {
        state.attribution.source = 'Current trusted landing';
        state.attribution.record = candidateRecord;
        persistRecord(candidateRecord);
        return;
      }
    }

    state.attribution.source = 'None until cart is checked';
    state.attribution.record = null;
  };

  const routesRoot = () => {
    const candidate = window.Shopify?.routes?.root;
    return typeof candidate === 'string' && candidate.startsWith('/') ? candidate : '/';
  };

  const cartEndpoint = (path) => `${routesRoot()}${path}`;

  const fetchCart = async () => {
    const response = await window.fetch(cartEndpoint('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
    return response.json();
  };

  const updateCartAttributes = async (attributes) => {
    const response = await window.fetch(cartEndpoint('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) throw new Error(`Cart write failed (${response.status})`);
    return response.json();
  };

  const queueCartOperation = (operation) => {
    const next = cartQueue.then(operation, operation);
    cartQueue = next.catch(() => undefined);
    return next;
  };

  const updateCartDiagnostic = (cart, status) => {
    const attributes = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
    const owned = ownedAttributesFrom(attributes);
    state.cart.status = status;
    state.cart.ownedAttributes = owned;
    state.cart.unrelatedAttributeCount = Object.keys(attributes).filter(
      (key) => !core.OWNED_ATTRIBUTE_KEYS.includes(key),
    ).length;
    state.cart.lastError = '';
    renderDiagnostics();
  };

  const synchronizeAttribution = (journey = {}) => {
    if (!marketingConsentIsCurrent()) {
      state.cart.status = 'Skipped: marketing not allowed; clearing any prior attribution';
      renderDiagnostics();
      return consentCleanupPromise.then(() => ({ status: 'skipped', cart: null }));
    }

    const epoch = consentEpoch;
    const superseded = () => ({ status: 'superseded', cart: null });
    return queueCartOperation(async () => {
      if (!marketingConsentIsCurrent(epoch)) return superseded();
      state.cart.status = 'Reading cart';
      state.cart.lastError = '';
      renderDiagnostics();

      try {
        const cart = await fetchCart();
        if (!marketingConsentIsCurrent(epoch)) return superseded();
        const selection = core.chooseFirstTouch({
          cartAttributes: cart.attributes,
          storedRecord: storageRecord,
          candidateRecord,
        });

        if (!selection.record) {
          state.attribution.source = 'None';
          state.attribution.record = null;
          updateCartDiagnostic(cart, 'Skipped: no approved first touch');
          return { status: 'skipped', cart };
        }

        const selectedRecord = ensureJourneyId(selection.record);
        state.attribution.source = selection.source;
        state.attribution.record = selectedRecord;
        persistRecord(selectedRecord);
        reconcileTouchOrigin(cart.attributes);

        const attributes = mergeAttributedCart(cart.attributes, journey);
        if (!attributes) throw new Error('Attribution payload was rejected');

        const attributesChanged = core.OWNED_ATTRIBUTE_KEYS.some(
          (key) => (cart.attributes?.[key] || '') !== (attributes[key] || ''),
        );
        if (!attributesChanged) {
          updateCartDiagnostic(cart, 'Already synchronized');
          return { status: 'unchanged', cart, record: selectedRecord };
        }

        state.cart.status = 'Writing cart attributes';
        renderDiagnostics();
        if (!marketingConsentIsCurrent(epoch)) return superseded();
        const updatedCart = await updateCartAttributes(attributes);
        if (!marketingConsentIsCurrent(epoch)) return superseded();
        const persistedRecord = core.firstTouchFromCartAttributes(updatedCart.attributes);
        if (!persistedRecord) throw new Error('Shopify did not return a valid first-touch record');

        const expectedJourney = core.normalizeJourney(journey);
        const journeyMismatch = Object.entries(expectedJourney).some(
          ([key, value]) => updatedCart.attributes?.[key] !== value,
        );
        if (journeyMismatch) throw new Error('Shopify did not return the expected journey attributes');

        updateCartDiagnostic(updatedCart, 'Synchronized');
        return { status: 'synchronized', cart: updatedCart, record: persistedRecord };
      } catch (error) {
        if (!marketingConsentIsCurrent(epoch)) return superseded();
        state.cart.status = 'Synchronization failed';
        state.cart.lastError = error instanceof Error ? error.message : 'Unknown cart error';
        renderDiagnostics();
        throw error;
      }
    });
  };

  const primeCartAttribution = () => {
    if (
      cartPrimeStarted ||
      window.Shopify?.designMode ||
      !marketingConsentIsCurrent()
    ) {
      return;
    }

    cartPrimeStarted = true;
    const epoch = consentEpoch;
    const token = isCartPage ? pendingOfferToken() : null;
    // The rendered cart snapshot can predate a product's background write.
    // Only a pending same-tab choice may override the freshly fetched cart;
    // passing the rendered journey here could write its older format back.
    synchronizeAttribution(isCartPage ? pendingOffer() || {} : {}).then((result) => {
      if (!marketingConsentIsCurrent(epoch)) return;
      consumeSyncedOffer(token, result);
    }).catch(() => {
      // Priming is opportunistic. The cart permalink remains the reliable
      // click-time fallback and must never make a shopper wait.
    }).finally(() => {
      // A superseded prime must not unlock publication for a later consent
      // epoch whose own cart reconciliation is still pending.
      if (!marketingConsentIsCurrent(epoch)) return;
      touchOriginReconciled = true;
      publishPendingAdTouches();
    });
  };

  const clearCartAttribution = () =>
    queueCartOperation(async () => {
      state.cart.status = 'Clearing attribution attributes';
      state.cart.lastError = '';
      renderDiagnostics();

      try {
        const cart = await fetchCart();
        const updatedCart = await updateCartAttributes(core.clearOwnedAttributes(cart.attributes));
        const remainingOwned = ownedAttributesFrom(updatedCart.attributes);
        if (Object.keys(remainingOwned).length) throw new Error('Some attribution attributes were not cleared');
        updateCartDiagnostic(updatedCart, 'Attribution test state cleared');
        return updatedCart;
      } catch (error) {
        state.cart.status = 'Reset failed';
        state.cart.lastError = error instanceof Error ? error.message : 'Unknown cart error';
        renderDiagnostics();
        throw error;
      }
    });

  const readCartForDiagnostics = () =>
    queueCartOperation(async () => {
      state.cart.status = 'Reading cart';
      state.cart.lastError = '';
      renderDiagnostics();

      try {
        const cart = await fetchCart();
        updateCartDiagnostic(cart, 'Read complete');
        return cart;
      } catch (error) {
        state.cart.status = 'Cart read failed';
        state.cart.lastError = error instanceof Error ? error.message : 'Unknown cart error';
        renderDiagnostics();
        throw error;
      }
    });

  const revokeAttribution = () => {
    consentEpoch += 1;
    cartPrimeStarted = false;
    cartSnapshotUsable = false;
    state.attribution.source = 'None';
    state.attribution.record = null;
    state.cart.ownedAttributes = {};
    candidateRecord = null;
    removeStoredRecord();
    consentCleanupPromise = clearCartAttribution().catch(() => undefined);
  };

  const privacyUnavailable = (status, consent) => {
    state.privacy = {
      status,
      region: 'Undetermined',
      shouldShowBanner: null,
      marketingAllowed: false,
      analyticsAllowed: false,
      preferencesAllowed: false,
      saleOfDataAllowed: false,
    };
    state.attribution.consent = consent;
    revokeAttribution();
    renderDiagnostics();
  };

  const applyPrivacyState = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) {
      privacyUnavailable('unavailable', 'Unavailable: attribution disabled');
      return;
    }

    const previouslyAllowed = state.privacy.marketingAllowed;
    try {
      state.privacy = {
        status: 'ready',
        region: privacy.getRegion() || 'Undetermined',
        shouldShowBanner: privacy.shouldShowBanner(),
        marketingAllowed: privacy.marketingAllowed() === true,
        analyticsAllowed: privacy.analyticsProcessingAllowed() === true,
        preferencesAllowed: privacy.preferencesProcessingAllowed() === true,
        saleOfDataAllowed: privacy.saleOfDataAllowed() === true,
      };
    } catch (_error) {
      privacyUnavailable('error', 'Privacy API failed: attribution disabled');
      return;
    }

    if (state.privacy.marketingAllowed) {
      if (!previouslyAllowed) {
        consentEpoch += 1;
        cartPrimeStarted = false;
      }
      state.attribution.consent = 'Marketing allowed';
      initializeFirstTouch();
      initializeAdTouches();
      if (!state.privacy.analyticsAllowed) {
        touchCache.pending = [];
        persistTouchCache();
      }
      primeCartAttribution();
      publishPendingAdTouches();
    } else {
      state.attribution.consent = 'Marketing not allowed';
      revokeAttribution();
    }

    renderDiagnostics();
    publishFunnelViewOnce();
  };

  const loadPrivacyApi = () => {
    if (!window.Shopify?.loadFeatures) {
      privacyUnavailable('unavailable', 'Shopify privacy loader unavailable');
      return;
    }

    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      (error) => {
        if (error) {
          privacyUnavailable('error', 'Privacy API failed: attribution disabled');
          return;
        }

        applyPrivacyState();
        document.addEventListener('visitorConsentCollected', applyPrivacyState);
      },
    );
  };

  const navigateOffer = (button) => {
    const action = core?.normalizeAction(button.dataset, state.profile);
    const clickAttributes =
      marketingConsentIsCurrent()
        ? mergeAttributedCart({}, action?.journey)
        : null;
    const destination = core?.buildDestination(action, routesRoot(), clickAttributes);

    if (!action || !destination) {
      state.cart.status = 'Blocked invalid server action';
      state.cart.lastError = 'The configured offer destination did not pass validation';
      renderDiagnostics();
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    // A later landing/upsell choice supersedes unsynced product intent, even
    // when both share the same preserved first-touch journey.
    if (!window.Shopify?.designMode && marketingConsentIsCurrent()) forgetPendingOffer();

    // Enqueue the custom event synchronously, then leave immediately. Direct
    // cart destinations carry the complete trusted attribution payload in the
    // Shopify-supported cart permalink itself, so no pre-navigation network
    // request is required.
    publishOfferClick(action);

    if (window.Shopify?.designMode) {
      synchronizeAttribution(action.journey)
        .then(() => {
          if (!state.cart.lastError) {
            state.cart.status = 'Synchronized; navigation skipped in theme editor';
          }
        })
        .catch(() => undefined)
        .finally(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          renderDiagnostics();
        });
      return;
    }

    window.location.assign(destination);
  };

  const productVariant = (form) => {
    try {
      const variants = JSON.parse(document.querySelector('[data-sm-product-variants]')?.textContent || '[]');
      const id = form.querySelector('[data-product-form-variant]')?.value;
      const variant = Array.isArray(variants) && variants.find((item) => item.id === id);
      if (!variant || variant.available !== true || !/^[0-9]{1,24}$/.test(variant.id)) return null;
      const format = core.productFormat(variant.options);
      return format ? { id: variant.id, format } : null;
    } catch (_error) { return null; }
  };

  const bindProductControls = (root) => {
    if (state.profile?.surface !== 'product') return;

    root.querySelectorAll('starmist-product form.product__form, starmist-product form.product-sticky-purchase__form').forEach((form) => {
      if (boundProductControls.has(form)) return;
      boundProductControls.add(form);
      form.addEventListener('submit', (event) => {
        if (event.submitter && !event.submitter.matches('[data-add-to-cart], [data-sticky-add-to-cart]')) return;
        const variant = productVariant(form);
        if (!variant) return;
        const sticky = form.classList.contains('product-sticky-purchase__form');
        publishOfferClick({
          type: 'direct-cart',
          variantId: variant.id,
          purchaseMethod: 'add-to-cart',
          journey: { selectedFormat: variant.format },
          cta: { id: sticky ? 'product-add-sticky' : 'product-add', placement: sticky ? 'sticky-purchase' : 'product-purchase' },
        });
        if (!window.Shopify?.designMode && marketingConsentIsCurrent()) {
          rememberProductOffer(variant.format);
          // Keep recovery through navigation even when this write succeeds:
          // cart HTML may already have rendered an older format. The cart
          // consumes the token after recovery; expiry/revocation still apply.
          synchronizeAttribution({ selectedFormat: variant.format }).catch(() => {});
        }
        // Observe only. OCU and native Shopify own the submit/checkout behavior.
      }, { capture: true });
    });

    root.querySelectorAll('starmist-product starmist-excerpt-disclosure summary').forEach((summary) => {
      if (boundProductControls.has(summary)) return;
      boundProductControls.add(summary);
      summary.addEventListener('click', () => {
        const details = summary.closest('details');
        if (!details || (details.open && details.dataset.excerptState !== 'closing')) return;
        publishChapterStart({ dataset: { smCtaId: 'product-sample', smCtaPlacement: 'product-excerpt' } });
      }, { capture: true });
    });

    root.querySelectorAll('starmist-product .product__description a[href]').forEach((link) => {
      if (boundProductControls.has(link) ||
        (!/\bprefer audio\b/i.test(link.textContent || '') && link.dataset.smAudioLink !== 'true')) return;
      try { if (new URL(link.href).protocol !== 'https:') return; } catch (_error) { return; }
      boundProductControls.add(link);
      const trackAudio = (event) => {
        if (event.type === 'auxclick' && event.button !== 1) return;
        publishOfferClick({
          type: 'external', journey: { selectedFormat: 'audio' },
          cta: { id: 'product-audio', placement: 'product-description' },
        });
      };
      link.addEventListener('click', trackAudio);
      link.addEventListener('auxclick', trackAudio);
    });
  };

  const bindInteractions = () => {
    const bindOfferButtons = (root) => {
      root.querySelectorAll?.('[data-sm-offer-action]').forEach((button) => {
        if (boundOfferButtons.has(button)) return;
        boundOfferButtons.add(button);
        button.addEventListener('click', () => navigateOffer(button));
      });
    };

    const bindChapterLinks = (root) => {
      root.querySelectorAll?.('[data-sm-chapter-action]').forEach((link) => {
        if (boundChapterLinks.has(link)) return;
        boundChapterLinks.add(link);
        link.addEventListener('click', () => publishChapterStart(link));
      });
    };

    bindOfferButtons(document);
    bindChapterLinks(document);
    bindProductControls(document);
    cartForm?.addEventListener('submit', updateCartFormFields, { capture: true });
    document.addEventListener('shopify:section:load', (event) => {
      bindOfferButtons(event.target);
      bindChapterLinks(event.target);
      bindProductControls(event.target);
    });

    document.querySelectorAll('[data-sm-diagnostic-sync]').forEach((button) => {
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          await synchronizeAttribution();
        } catch (_error) {
          // The visible diagnostic reports the failure.
        } finally {
          button.disabled = false;
        }
      });
    });

    document.querySelectorAll('[data-sm-diagnostic-read-cart]').forEach((button) => {
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          await readCartForDiagnostics();
        } catch (_error) {
          // The visible diagnostic reports the failure.
        } finally {
          button.disabled = false;
        }
      });
    });

    document.querySelectorAll('[data-sm-diagnostic-reset]').forEach((button) => {
      button.addEventListener('click', async () => {
        button.disabled = true;
        consentEpoch += 1;
        captureSuppressedAfterReset = true;
        candidateRecord = null;
        state.attribution.source = 'None';
        state.attribution.record = null;
        state.cart.ownedAttributes = {};
        removeStoredRecord();
        renderDiagnostics();

        try {
          await clearCartAttribution();
        } catch (_error) {
          // The visible diagnostic reports the failure.
        } finally {
          button.disabled = false;
        }
      });
    });
  };

  document.documentElement.dataset.smLandingFoundation = 'ready';
  document.documentElement.dataset.smAttribution = 'initializing';
  state.profile = parseTrustedProfile();

  if (!core || (!state.profile && !isCartPage)) {
    state.privacy.status = 'error';
    state.attribution.consent = 'Trusted profile invalid: attribution disabled';
    state.cart.lastError = 'The server-rendered profile did not pass validation';
    document.documentElement.dataset.smAttribution = 'error';
    renderDiagnostics();
    return;
  }

  bindInteractions();
  renderDiagnostics();
  loadPrivacyApi();
  document.documentElement.dataset.smAttribution = 'ready';
})();
